import { useState } from 'react';
import { AlertTriangle, Trash2, AlertCircle } from 'lucide-react';
import { Modal } from './Modal';
import { Button } from './Button';
import { Input } from './Input';
import { cn } from '../../utils/cn';

type DialogVariant = 'danger' | 'warning' | 'info';

interface ConfirmationDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (reason?: string) => void | Promise<void>;
  title: string;
  message: string;
  confirmLabel?: string;
  cancelLabel?: string;
  variant?: DialogVariant;
  requireReason?: boolean;
  reasonLabel?: string;
  reasonPlaceholder?: string;
  isLoading?: boolean;
}

export function ConfirmationDialog({
  isOpen,
  onClose,
  onConfirm,
  title,
  message,
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  variant = 'warning',
  requireReason = false,
  reasonLabel = 'Reason',
  reasonPlaceholder = 'Please provide a reason for this action',
  isLoading = false,
}: ConfirmationDialogProps) {
  const [reason, setReason] = useState('');
  const [error, setError] = useState('');

  const handleConfirm = async () => {
    if (requireReason && !reason.trim()) {
      setError('Please provide a reason');
      return;
    }
    setError('');
    await onConfirm(requireReason ? reason : undefined);
    setReason('');
  };

  const handleClose = () => {
    setReason('');
    setError('');
    onClose();
  };

  const icons = {
    danger: Trash2,
    warning: AlertTriangle,
    info: AlertCircle,
  };

  const iconColors = {
    danger: 'bg-red-100 text-red-600',
    warning: 'bg-yellow-100 text-yellow-600',
    info: 'bg-primary-100 text-primary-600 dark:bg-primary-900/30 dark:text-primary-400',
  };

  const buttonVariants = {
    danger: 'danger' as const,
    warning: 'primary' as const,
    info: 'primary' as const,
  };

  const Icon = icons[variant];

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      ariaLabel={title}
      size="sm"
      showCloseButton={false}
      closeOnOverlayClick={!isLoading}
      closeOnEscape={!isLoading}
    >
      <div className="text-center">
        {/* Icon */}
        <div
          className={cn(
            'mx-auto w-12 h-12 rounded-full flex items-center justify-center mb-4',
            iconColors[variant]
          )}
        >
          <Icon className="w-6 h-6" />
        </div>

        {/* Title & Message */}
        <h3 className="text-lg font-semibold text-gray-900 dark:text-[var(--amy-text)] mb-2">
          {title}
        </h3>
        <p className="text-sm text-gray-500 dark:text-[var(--amy-muted)] mb-6">
          {message}
        </p>

        {/* Reason Input */}
        {requireReason && (
          <div className="mb-6 text-left">
            <Input
              label={reasonLabel}
              name="confirmation-reason"
              placeholder={reasonPlaceholder}
              value={reason}
              onChange={e => setReason(e.target.value)}
              error={error}
              disabled={isLoading}
            />
          </div>
        )}

        {/* Actions */}
        <div className="flex gap-3">
          <Button
            variant="outline"
            className="flex-1"
            onClick={handleClose}
            disabled={isLoading}
          >
            {cancelLabel}
          </Button>
          <Button
            variant={buttonVariants[variant]}
            className="flex-1"
            onClick={handleConfirm}
            isLoading={isLoading}
          >
            {confirmLabel}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
